import { cn } from '@/lib/utils';

/**
 * Per-module icon colours — derived from the dashboard hero-tile gradients
 * (Dashboard.jsx heroCards), keyed by the module SVG path. The header/sidebar
 * menu glyphs are tinted directly in their module's colour (no chip behind),
 * with a lighter variant for dark mode so they stay readable.
 *
 * The module SVGs are single-colour glyphs, so we paint them via a CSS mask:
 * the span's background-color shows through the glyph's silhouette. Values are
 * full literal Tailwind classes so the JIT keeps them.
 */
export const MODULE_ICON_COLOR = {
    '/module-icons/ai-documents.svg':       'bg-[#E68A00] dark:bg-[#FBA53A]',
    '/module-icons/manual-photo-edit.svg':  'bg-[#0067ff] dark:bg-[#57a6ff]',
    '/module-icons/studio-editor.svg':      'bg-[#0d9488] dark:bg-[#2dd4bf]',
    '/module-icons/bulk-photo-editing.svg': 'bg-[#10b518] dark:bg-[#4ade80]',
    '/module-icons/document-set.svg':       'bg-[#725aff] dark:bg-[#a78bfa]',
    '/module-icons/job.svg':                'bg-[#ff9000] dark:bg-[#ffb02e]',
    '/module-icons/ai-templates.svg':       'bg-[#7c3aed] dark:bg-[#a78bfa]',
    '/module-icons/print-media.svg':        'bg-[#ea580c] dark:bg-[#fb923c]',
    '/module-icons/easy-tools.svg':         'bg-[#ef465f] dark:bg-[#f87f92]',
    '/questions-create.svg':                'bg-[#d62828] dark:bg-[#f26d6d]',
};

// "আমার ডকুমেন্ট" has no module SVG — its Tabler icon is tinted with this
// text colour (mirrors the dashboard's documents stat-card blue).
export const MY_DOCS_ICON_COLOR = 'text-[#3a86ff] dark:text-[#6ea8ff]';

// SVGs that already carry their own colours (e.g. the orange "Ai" glyph, the Studio Editor's teal camera + amber "Ai" badge) —
// rendered raw so the menu shows their own colours instead of a single-colour mask tint.
const SELF_COLORED = new Set(['/module-icons/ai-tools.svg', '/module-icons/studio-editor.svg']);
export const isSelfColored = (src) => SELF_COLORED.has(src);
// A few modules swap to a different glyph ON A TILE (the Studio Editor's tile is teal, so its camera turns dark there; the menus keep the teal one).
const TILE_GLYPH = { '/module-icons/studio-editor.svg': '/module-icons/studio-editor-tile.svg' };
export const tileGlyphSrc = (src) => TILE_GLYPH[src] || src;
// …and draw it larger than the usual 60%: its canvas keeps room for the sparkle corner, so the camera itself would look small.
const TILE_GLYPH_SIZE = { '/module-icons/studio-editor.svg': 'h-[72%] w-[72%]', '/module-icons/studio-editor-tile.svg': 'h-[72%] w-[72%]' };
export const tileGlyphSize = (src) => TILE_GLYPH_SIZE[src] || 'h-3/5 w-3/5';

/**
 * ModuleGlyph — a module SVG rendered directly in its brand colour.
 * size: 'sm' = header tabs (18px), 'md' = sidebar rows (19px).
 */
export function ModuleGlyph({ src, size = 'sm', className }) {
    if (SELF_COLORED.has(src)) {
        return (
            <img
                src={src}
                alt=""
                draggable="false"
                className={cn(
                    'object-contain shrink-0',
                    size === 'md' ? 'w-[19px] h-[19px]' : 'w-[18px] h-[18px]',
                    className
                )}
            />
        );
    }
    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-block shrink-0',
                size === 'md' ? 'w-[19px] h-[19px]' : 'w-[18px] h-[18px]',
                MODULE_ICON_COLOR[src] || 'bg-zinc-500',
                className
            )}
            style={{
                WebkitMaskImage: `url(${src})`,
                maskImage: `url(${src})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
            }}
        />
    );
}
